/**
 * LCD Eligibility Widget - Compact Patient Eligibility Checker
 * 
 * A sidebar widget that provides quick hospice eligibility assessment
 * in the patient context. Auto-populates patient data and provides
 * one-click evaluation with visual status display.
 */

import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  ClipboardCheck, CheckCircle, XCircle, AlertTriangle,
  ChevronRight, Loader2, Activity, Heart, Brain,
  Stethoscope, FileText, ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// LCD Category Icons & Colors
const CATEGORY_CONFIG = {
  cancer: { icon: "🎗️", color: "text-pink-400", bg: "bg-pink-500/10" },
  dementia: { icon: "🧠", color: "text-purple-400", bg: "bg-purple-500/10" },
  heart_disease: { icon: "❤️", color: "text-red-400", bg: "bg-red-500/10" },
  pulmonary: { icon: "🫁", color: "text-blue-400", bg: "bg-blue-500/10" },
  renal: { icon: "🫘", color: "text-amber-400", bg: "bg-amber-500/10" },
  liver: { icon: "🔶", color: "text-orange-400", bg: "bg-orange-500/10" },
  als: { icon: "🧬", color: "text-cyan-400", bg: "bg-cyan-500/10" },
  stroke: { icon: "⚡", color: "text-yellow-400", bg: "bg-yellow-500/10" },
  hiv_aids: { icon: "🔬", color: "text-violet-400", bg: "bg-violet-500/10" },
  debility: { icon: "📉", color: "text-slate-400", bg: "bg-slate-500/10" }
};

export default function LCDEligibilityWidget({ 
  patient, 
  isDark = true, 
  compact = false,
  onEvaluationComplete 
}) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  // States
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [kpsScore, setKpsScore] = useState(70);
  const [evaluating, setEvaluating] = useState(false);
  const [lastEvaluation, setLastEvaluation] = useState(null);
  const [showQuickEval, setShowQuickEval] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/lcd-criteria/categories`);
        if (res.ok) {
          const data = await res.json();
          setCategories(data.categories || []);
        }
      } catch (error) {
        console.error("Failed to fetch LCD categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch last evaluation for patient
  useEffect(() => {
    if (!patient?.id) {
      setLastEvaluation(null);
      return;
    }

    const fetchLastEvaluation = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${API_URL}/api/lcd-criteria/patient/${patient.id}/evaluations?limit=1`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) {
          const data = await res.json();
          if (data.evaluations?.length > 0) {
            setLastEvaluation(data.evaluations[0]);
          }
        }
      } catch (error) {
        console.error("Failed to fetch evaluation:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLastEvaluation();
  }, [patient?.id, token]);

  // Quick evaluation
  const runQuickEvaluation = async () => {
    if (!selectedCategory || !patient?.id) {
      toast.error("Select a diagnosis category");
      return;
    }

    try {
      setEvaluating(true);
      const res = await fetch(`${API_URL}/api/lcd-criteria/evaluate/comprehensive`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: patient.id,
          primary_diagnosis_icd10: patient.diagnosis?.icd10 || "unknown",
          kps_score: kpsScore,
          pps_score: kpsScore,
          clinical_indicators: [],
          documentation_present: []
        })
      });

      if (res.ok) {
        const result = await res.json();
        setLastEvaluation({
          ...result,
          diagnosis_category: selectedCategory,
          created_at: new Date().toISOString()
        });
        
        toast.success(
          result.hospice_eligible 
            ? "Patient meets hospice eligibility criteria" 
            : "Patient may not meet all criteria"
        );
        
        onEvaluationComplete?.(result);
        setShowQuickEval(false);
      }
    } catch (error) {
      toast.error("Evaluation failed");
    } finally {
      setEvaluating(false);
    }
  };

  // Compact view (for sidebar)
  if (compact) {
    return (
      <div className="space-y-3" data-testid="lcd-eligibility-widget-compact">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-emerald-400" />
            <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? "text-slate-400" : "text-gray-500"}`}>
              LCD Eligibility
            </span>
          </div>
          {lastEvaluation && (
            <Badge className={
              lastEvaluation.hospice_eligible
                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs"
                : "bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs"
            }>
              {lastEvaluation.hospice_eligible ? "Eligible" : "Review"}
            </Badge>
          )}
        </div>

        {patient ? (
          <div className={`p-3 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-gray-100"}`}>
            {lastEvaluation ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {lastEvaluation.hospice_eligible ? (
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                  )}
                  <span className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                    {lastEvaluation.hospice_eligible ? "Hospice Eligible" : "Criteria Review Needed"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className={isDark ? "text-slate-400" : "text-gray-500"}>
                    {CATEGORY_CONFIG[lastEvaluation.diagnosis_category]?.icon} {lastEvaluation.primary_diagnosis?.lcd_name || lastEvaluation.diagnosis_category}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <Activity className="w-3 h-3 text-blue-400" />
                  <span className={isDark ? "text-slate-400" : "text-gray-500"}>
                    KPS: {lastEvaluation.functional_assessment?.kps_score || "N/A"}
                  </span>
                </div>
                <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                  Evaluated: {new Date(lastEvaluation.created_at || lastEvaluation.evaluated_at).toLocaleDateString()}
                </p>
              </div>
            ) : (
              <div className="text-center py-2">
                <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                  No evaluation on file
                </p>
              </div>
            )}
            
            <div className="flex gap-2 mt-3">
              <Button 
                size="sm" 
                variant="outline"
                className="flex-1 text-xs"
                onClick={() => setShowQuickEval(true)}
              >
                Quick Eval
              </Button>
              <Button 
                size="sm" 
                className="flex-1 text-xs bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate("/lcd-eligibility")}
              >
                Full Check
                <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        ) : (
          <div className={`p-3 rounded-lg text-center ${isDark ? "bg-slate-800/50" : "bg-gray-100"}`}>
            <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>
              Select a patient to check eligibility
            </p>
          </div>
        )}

        {/* Quick Evaluation Sheet */}
        <Sheet open={showQuickEval} onOpenChange={setShowQuickEval}>
          <SheetContent side="right" className={`w-96 ${isDark ? "bg-slate-900 border-slate-800" : "bg-white"}`}>
            <SheetHeader>
              <SheetTitle className={`flex items-center gap-2 ${isDark ? "text-white" : ""}`}>
                <ClipboardCheck className="w-5 h-5 text-emerald-400" />
                Quick LCD Evaluation
              </SheetTitle>
              <SheetDescription>
                {patient?.name} - Quick hospice eligibility check
              </SheetDescription>
            </SheetHeader>
            
            <div className="mt-6 space-y-6">
              {/* Category Selection */}
              <div>
                <label className={`text-xs font-medium mb-2 block ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                  Primary Diagnosis Category
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {categories.slice(0, 5).map((cat) => (
                    <button
                      key={cat.key}
                      onClick={() => setSelectedCategory(cat.key)}
                      className={`p-2 rounded-lg border text-center transition-all ${
                        selectedCategory === cat.key
                          ? "border-emerald-500 bg-emerald-500/10"
                          : isDark 
                            ? "border-slate-700 bg-slate-800 hover:border-slate-600" 
                            : "border-gray-200 bg-gray-50 hover:border-gray-300"
                      }`}
                    >
                      <span className="text-xl">{CATEGORY_CONFIG[cat.key]?.icon}</span>
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-5 gap-2 mt-2">
                  {categories.slice(5, 10).map((cat) => (
                    <button
                      key={cat.key}
                      onClick={() => setSelectedCategory(cat.key)}
                      className={`p-2 rounded-lg border text-center transition-all ${
                        selectedCategory === cat.key
                          ? "border-emerald-500 bg-emerald-500/10"
                          : isDark 
                            ? "border-slate-700 bg-slate-800 hover:border-slate-600" 
                            : "border-gray-200 bg-gray-50 hover:border-gray-300"
                      }`}
                    >
                      <span className="text-xl">{CATEGORY_CONFIG[cat.key]?.icon}</span>
                    </button>
                  ))}
                </div>
                {selectedCategory && (
                  <p className={`text-xs mt-2 ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                    Selected: {categories.find(c => c.key === selectedCategory)?.name}
                  </p>
                )}
              </div>

              {/* KPS Score */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={`text-xs font-medium ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                    KPS/PPS Score
                  </label>
                  <span className={`text-xl font-bold ${kpsScore <= 70 ? "text-emerald-400" : "text-amber-400"}`}>
                    {kpsScore}
                  </span>
                </div>
                <Slider
                  value={[kpsScore]}
                  onValueChange={(v) => setKpsScore(v[0])}
                  min={0}
                  max={100}
                  step={10}
                />
                <div className="flex justify-between mt-1">
                  <span className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>0</span>
                  <span className={`text-xs ${kpsScore <= 70 ? "text-emerald-400" : "text-amber-400"}`}>
                    Hospice: ≤70
                  </span>
                  <span className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>100</span>
                </div>
              </div>

              {/* Quick Status */}
              <div className={`p-4 rounded-lg ${
                selectedCategory && kpsScore <= 70
                  ? "bg-emerald-500/10 border border-emerald-500/30"
                  : "bg-slate-800 border border-slate-700"
              }`}>
                <div className="flex items-center gap-3">
                  {selectedCategory && kpsScore <= 70 ? (
                    <CheckCircle className="w-6 h-6 text-emerald-400" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 text-slate-400" />
                  )}
                  <div>
                    <p className={`font-medium ${
                      selectedCategory && kpsScore <= 70 ? "text-emerald-400" : isDark ? "text-slate-300" : "text-gray-700"
                    }`}>
                      {selectedCategory && kpsScore <= 70 
                        ? "Likely Eligible" 
                        : "Complete Selection"
                      }
                    </p>
                    <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                      {selectedCategory 
                        ? `${CATEGORY_CONFIG[selectedCategory]?.icon} ${categories.find(c => c.key === selectedCategory)?.name} • KPS ${kpsScore}`
                        : "Select diagnosis category"
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowQuickEval(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={runQuickEvaluation}
                  disabled={!selectedCategory || evaluating}
                >
                  {evaluating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ClipboardCheck className="w-4 h-4 mr-2" />
                  )}
                  Evaluate
                </Button>
              </div>

              <p className={`text-xs text-center ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                For comprehensive evaluation with clinical indicators, use{" "}
                <button 
                  className="text-emerald-400 hover:underline"
                  onClick={() => {
                    setShowQuickEval(false);
                    navigate("/lcd-eligibility");
                  }}
                >
                  Full LCD Checker
                </button>
              </p>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  // Full card view (for dashboards)
  return (
    <Card className={`${isDark ? "bg-slate-900 border-slate-800" : "bg-white"}`} data-testid="lcd-eligibility-widget">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-emerald-400" />
            <span className={`font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
              LCD Eligibility Status
            </span>
          </div>
          {lastEvaluation && (
            <Badge className={
              lastEvaluation.hospice_eligible
                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                : "bg-amber-500/20 text-amber-400 border-amber-500/30"
            }>
              {lastEvaluation.hospice_eligible ? "Eligible" : "Review Needed"}
            </Badge>
          )}
        </div>

        {patient ? (
          <>
            {lastEvaluation ? (
              <div className="space-y-3">
                <div className={`p-3 rounded-lg ${
                  lastEvaluation.hospice_eligible 
                    ? "bg-emerald-500/10 border border-emerald-500/30" 
                    : "bg-amber-500/10 border border-amber-500/30"
                }`}>
                  <div className="flex items-center gap-3">
                    {lastEvaluation.hospice_eligible ? (
                      <CheckCircle className="w-6 h-6 text-emerald-400" />
                    ) : (
                      <AlertTriangle className="w-6 h-6 text-amber-400" />
                    )}
                    <div>
                      <p className={`font-medium ${lastEvaluation.hospice_eligible ? "text-emerald-400" : "text-amber-400"}`}>
                        {lastEvaluation.hospice_eligible 
                          ? "Patient Meets Hospice Eligibility Criteria" 
                          : "Additional Documentation May Be Required"
                        }
                      </p>
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        {lastEvaluation.primary_diagnosis?.lcd_name || lastEvaluation.diagnosis_category}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                    <p className="text-lg font-bold text-blue-400">
                      {lastEvaluation.functional_assessment?.kps_score || "N/A"}
                    </p>
                    <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-500"}`}>KPS Score</p>
                  </div>
                  <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                    <p className="text-lg font-bold text-purple-400">
                      {lastEvaluation.functional_assessment?.pps_score || "N/A"}%
                    </p>
                    <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-500"}`}>PPS Score</p>
                  </div>
                  <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                    <span className="text-2xl">
                      {CATEGORY_CONFIG[lastEvaluation.diagnosis_category]?.icon || "📋"}
                    </span>
                    <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-500"}`}>LCD</p>
                  </div>
                </div>

                <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                  Last evaluated: {new Date(lastEvaluation.created_at || lastEvaluation.evaluated_at).toLocaleString()}
                </p>
              </div>
            ) : (
              <div className="text-center py-4">
                <ClipboardCheck className={`w-10 h-10 mx-auto mb-2 ${isDark ? "text-slate-600" : "text-gray-300"}`} />
                <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                  No eligibility evaluation on file
                </p>
                <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                  Run an evaluation to assess hospice eligibility
                </p>
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowQuickEval(true)}
              >
                Quick Eval
              </Button>
              <Button 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate("/lcd-eligibility")}
              >
                Full Evaluation
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center py-6">
            <Stethoscope className={`w-10 h-10 mx-auto mb-2 ${isDark ? "text-slate-600" : "text-gray-300"}`} />
            <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
              Select a patient to view eligibility status
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
